import { useEffect, useState } from "react";
import { usePreferences } from "../../preferences/preferencesContext";
import { useSession } from "../../session/SessionContext";
import type { Theme } from "../../theme/useTheme";
import { NeuralField } from "../shared/NeuralField";
import styles from "./PageBackground.module.css";

const reducedMotionQuery = "(prefers-reduced-motion: reduce)";

/**
 * Whether the viewer's device asks for reduced motion, kept current if they change it while the page is open.
 *
 * @returns True when motion should be reduced.
 */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => typeof window.matchMedia === "function" && window.matchMedia(reducedMotionQuery).matches);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return;
    }
    const query = window.matchMedia(reducedMotionQuery);
    const onChange = (): void => setReduced(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  return reduced;
}

/**
 * The signed-in pages' background: the signed-out hero's network, drawn faintly behind everything in the theme's colours, drifting
 * on its own. It does not follow the pointer, so it never competes with what the person is pointing at. Shown to a signed-in person unless they have turned it off in Preferences (WCAG 2.2.2:
 * it moves for longer than five seconds, so it can be stopped), and drawn still for a device that asks for reduced motion. The
 * panels are opaque, so it shows between them, never behind their figures.
 *
 * @param props.theme - The theme in use, which sets the network's colours.
 */
export function PageBackground({ theme }: { theme: Theme }): React.JSX.Element | null {
  const { snapshot } = useSession();
  const { preferences } = usePreferences();
  const reducedMotion = usePrefersReducedMotion();

  if (snapshot.status !== "signed-in" || !preferences.animatedBackground) {
    return null;
  }

  return (
    <div className={styles.background} data-theme-tone={theme} aria-hidden="true">
      <NeuralField animate={!reducedMotion} tone={theme} interactive={false} />
    </div>
  );
}
